import React, { useState } from 'react';
import { CreditCard, Shield, Zap, HelpCircle, Globe } from 'lucide-react';
import { PricingPlans } from './PricingPlans';
import { UsageTracker } from './UsageTracker';
import { PricingPlan } from '@/config/pricing';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  RazorpayResponse,
  getPlanPrice,
  getPaymentMethods,
  isRazorpayConfigured,
  RAZORPAY_ERRORS
} from '@/config/razorpay';
import { authService } from '@/services/auth';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import apiService from '@/services/api';

const PricingPage: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { subscription, refreshSubscription } = useSubscription();
  const currentPlan = subscription?.plan_id || 'free';

  const handleSelectPlan = async (plan: PricingPlan) => {
    setSelectedPlan(plan);
    // Directly trigger payment without showing popup
    await handlePaymentInitiation(plan);
  };

  const handleUpgrade = () => {
    setShowUpgradeDialog(true);
  };

  const handlePaymentInitiation = async (plan: PricingPlan) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      toast.error('Please login to subscribe to a plan');
      window.location.href = '/login';
      return;
    }

    if (!isRazorpayConfigured()) {
      toast.error(RAZORPAY_ERRORS.NOT_CONFIGURED);
      return;
    }

    // ✅ Access Razorpay from window object instead of useRazorpay hook
    const RazorpaySDK = (window as any).Razorpay;
    if (!RazorpaySDK) {
      toast.error('Payment system not loaded. Please refresh the page.');
      return;
    }

    const displayCurrency = 'USD';
    const paymentCurrency = 'INR';
    const displayAmount = getPlanPrice(plan.id, displayCurrency, 'monthly');
    const inrAmount = Math.round(displayAmount * 83);

    if (inrAmount <= 0) {
      toast.error(RAZORPAY_ERRORS.INVALID_AMOUNT);
      return;
    }

    console.log(`[Pricing] Plan: ${plan.id}, Display: $${displayAmount}, Payment: ₹${inrAmount}`);

    const token = authService.getAccessToken();
    if (!token) {
      toast.error('Authentication token not found. Please login again.');
      window.location.href = '/login';
      return;
    }

    try {
      // Create subscription on backend
      console.log('[Pricing] Creating checkout session...');
      const paymentData = await apiService.createCheckoutSession({
        plan_id: plan.id,
        success_url: `${window.location.origin}/payment/success`,
        cancel_url: `${window.location.origin}/payment/cancel`,
      });

      console.log('[Pricing] Checkout session created:', {
        subscription_id: paymentData.subscription_id,
        amount: paymentData.amount,
        currency: paymentData.currency,
      });

      const userEmail = user?.email || 'user@example.com';
      const userName = user?.full_name || 'User';

      // ✅ CRITICAL FIX: Include currency and amount in options
      const options: any = {
        key: paymentData.key_id,
        subscription_id: paymentData.subscription_id,
        // ✅ CRITICAL: These fields are required for Razorpay SDK
        currency: paymentCurrency,
        amount: inrAmount * 100,  // Convert to paise

        name: 'Dytafly',
        description: `${plan.name} Plan Subscription`,
        prefill: {
          email: userEmail,
          name: userName,
        },
        theme: {
          color: '#3B82F6',
        },
        // ✅ Use proper payment methods helper function
        method: getPaymentMethods(paymentCurrency),
      };

      console.log('[Pricing] Razorpay options configured:', {
        subscription_id: options.subscription_id,
        currency: options.currency,
        amount: options.amount,
        methods: options.method,
      });

      // ✅ CRITICAL: Initialize Razorpay with callback handlers
      try {
        const razorpayInstance = new RazorpaySDK(options);

        // ✅ Register ALL event handlers BEFORE opening popup

        // Success handler - THIS WAS MISSING!
        razorpayInstance.on('payment.success', async (response: any) => {
          console.log('[Razorpay] 🎉 Payment SUCCESS event triggered!', response);
          await handlePaymentSuccess(response);
        });

        // Failure handler
        razorpayInstance.on('payment.failed', async (response: any) => {
          console.error('[Razorpay] ❌ Payment FAILED event triggered!', response);
          const errorMessage = response?.error?.description || 'Payment failed';
          toast.error(`Payment failed: ${errorMessage}`);

          // Log payment failure for debugging
          console.error('[Razorpay] Failure details:', {
            error_code: response?.error?.code,
            error_description: response?.error?.description,
            error_source: response?.error?.source,
            error_step: response?.error?.step,
            error_reason: response?.error?.reason,
          });
        });

        // Error handler
        razorpayInstance.on('payment.error', async (response: any) => {
          console.error('[Razorpay] ⚠️ Payment ERROR event triggered!', response);
          const errorMessage = response?.error?.description || 'Unknown error occurred';
          toast.error(`Payment error: ${errorMessage}`);
        });

        // Modal dismiss handler
        razorpayInstance.on('payment.closed', () => {
          console.log('[Razorpay] Modal dismissed by user');
        });

        console.log('[Razorpay] Opening checkout popup...');
        razorpayInstance.open();

      } catch (razorpayError: any) {
        console.error('[Razorpay] ❌ Failed to initialize Razorpay instance:', {
          error: razorpayError,
          errorMessage: razorpayError?.message,
          errorStack: razorpayError?.stack,
        });
        toast.error('Failed to open payment gateway. Please refresh and try again.');
      }

    } catch (error: any) {
      console.error('[Pricing] ❌ Payment initialization error:', {
        error,
        errorMessage: error?.message,
        errorResponse: error?.response?.data,
      });
      const errorMessage = error?.response?.data?.detail || error?.message || RAZORPAY_ERRORS.NETWORK_ERROR;
      toast.error(errorMessage);
    }
  };

  const handlePaymentSuccess = async (response: RazorpayResponse) => {
    if (!selectedPlan) {
      console.error('[Payment] No plan selected');
      toast.error('Plan information missing. Please try again.');
      return;
    }

    console.log('[Payment] Processing payment success for plan:', selectedPlan.id);

    const token = authService.getAccessToken();
    if (!token) {
      toast.error('Authentication token not found. Please login again.');
      window.location.href = '/login';
      return;
    }

    try {
      console.log('[Payment] Verifying payment with backend...');

      // ✅ CRITICAL: Call verify-payment endpoint
      const verifyResponse = await apiService.verifyPayment({
        razorpay_subscription_id: response.razorpay_subscription_id || '',
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
        plan_id: selectedPlan.id,
      });

      console.log('[Payment] ✅ Payment verified by backend:', verifyResponse);

      // ✅ CRITICAL: Refresh subscription context
      console.log('[Payment] Refreshing subscription context...');
      await refreshSubscription();

      // ✅ CRITICAL: Show success message
      setShowUpgradeDialog(false);
      toast.success(`🎉 Successfully subscribed to ${selectedPlan.name} plan!`);

      console.log('[Payment] ✅ Payment flow completed successfully');

    } catch (error: any) {
      console.error('[Payment] ❌ Payment verification failed:', {
        error,
        errorMessage: error?.message,
        errorResponse: error?.response?.data,
      });

      const errorMessage = error?.response?.data?.detail || error?.message || 'Payment verification failed';

      // Show detailed error message
      toast.error(
        `${errorMessage}\n\nPayment ID: ${response.razorpay_payment_id}\n\nPlease contact support if amount was deducted.`
      );

      // Log full error for debugging
      console.error('[Payment] Full error details:', error);
    }
  };

  const faqs = [
    {
      question: 'Can I change my plan anytime?',
      answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.',
    },
    {
      question: 'What happens if I exceed my limits?',
      answer: 'We\'ll notify you when you\'re approaching your limits. For requests, you\'ll need to upgrade. For tokens, queries may be throttled.',
    },
    {
      question: 'Do you offer custom enterprise plans?',
      answer: 'Yes! Contact our sales team for custom pricing, unlimited usage, and enterprise features.',
    },
    {
      question: 'Is there a free trial for paid plans?',
      answer: 'New users get 14 days to try any paid plan features with the free plan.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept UPI, Netbanking, Credit/Debit Cards, and Digital Wallets. All payments are in INR and processed securely through Razorpay.',
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="border-b border-[#e6e6ea] px-5 py-3 shrink-0">
        <h1 className="text-[18px] font-semibold text-[#22212d]">Pricing</h1>
      </div>

      {/* Page Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-[24px] font-medium text-[#22212d] mb-2">Choose Your Plan</h2>
          <p className="text-[14px] text-[rgba(34,33,45,0.6)] max-w-2xl mx-auto">
            Scale your SQL generation with flexible pricing that grows with your needs
          </p>
        </div>

        {/* Trust Badges */}
        <div className="flex items-center justify-center gap-8 mb-6">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-[rgba(34,33,45,0.6)]" />
            <span className="text-[13px] text-[rgba(34,33,45,0.6)]">SSL Encrypted</span>
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-[rgba(34,33,45,0.6)]" />
            <span className="text-[13px] text-[rgba(34,33,45,0.6)]">Razorpay Secured</span>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-[rgba(34,33,45,0.6)]" />
            <span className="text-[13px] text-[rgba(34,33,45,0.6)]">USD Pricing</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-[rgba(34,33,45,0.6)]" />
            <span className="text-[13px] text-[rgba(34,33,45,0.6)]">Instant Activation</span>
          </div>
        </div>

      {/* Tabs */}
      <Tabs defaultValue="plans" className="space-y-8">
        <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
          <TabsTrigger value="plans">Pricing Plans</TabsTrigger>
          <TabsTrigger value="usage">Current Usage</TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="space-y-8">
          <PricingPlans
            currentPlan={currentPlan}
            onSelectPlan={handleSelectPlan}
          />

          {/* FAQs */}
          <Card className="bg-white border-[#e6e6ea]">
            <CardHeader className="pb-4">
              <CardTitle className="text-[16px] font-semibold text-[#22212d] flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                Frequently Asked Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {faqs.map((faq, index) => (
                  <div key={index} className="space-y-2">
                    <h4 className="text-[14px] font-medium text-[#22212d]">{faq.question}</h4>
                    <p className="text-[13px] text-[rgba(34,33,45,0.6)]">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Enterprise CTA */}
          <Alert className="border-primary/20 bg-primary/5">
            <AlertDescription className="flex items-center justify-between">
              <div>
                <h4 className="text-[14px] font-medium text-[#22212d] mb-1">Need a custom solution?</h4>
                <p className="text-[13px] text-[rgba(34,33,45,0.6)]">
                  Get unlimited everything with custom pricing and dedicated support
                </p>
              </div>
              <Button variant="outline" className="ml-4 h-7 px-2.5 text-[13px] border-[#d8d8de]">
                Contact Sales
              </Button>
            </AlertDescription>
          </Alert>
        </TabsContent>

        <TabsContent value="usage" className="space-y-8">
          <UsageTracker onUpgrade={handleUpgrade} />

          {/* Quick Upgrade */}
          <Card className="bg-white border-[#e6e6ea]">
            <CardHeader className="pb-4">
              <CardTitle className="text-[16px] font-semibold text-[#22212d]">Need More?</CardTitle>
              <CardDescription className="text-[13px] text-[rgba(34,33,45,0.6)]">
                Upgrade your plan to unlock more requests, connections, and advanced features
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleUpgrade} variant="gradient">
                View Upgrade Options
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>

      {/* Upgrade Dialog - Payment is now handled inline in handlePaymentInitiation */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upgrade Your Plan</DialogTitle>
            <DialogDescription>
              Select a plan below to upgrade your subscription
            </DialogDescription>
          </DialogHeader>

          {selectedPlan && (
            <div className="py-4">
              <Card>
                <CardHeader>
                  <CardTitle>{selectedPlan.name} Plan</CardTitle>
                  <CardDescription>${getPlanPrice(selectedPlan.id, 'USD', 'monthly')}/month</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">{selectedPlan.description}</p>
                  <Button onClick={() => {
                    setShowUpgradeDialog(false);
                    handlePaymentInitiation(selectedPlan);
                  }} className="w-full">
                    Proceed to Payment
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingPage;